import { useState, useRef, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ProjectAnalyst } from './ProjectAnalyst';
import { GitIntegration } from './GitIntegration';
import { 
  FolderOpen,
  Plus,
  Calendar,
  Play,
  Archive,
  FileUp,
  RefreshCw,
  ChevronLeft,
  Terminal,
  FileText,
  Edit,
  GitBranch,
  Github,
  Gitlab,
  Server
} from 'lucide-preact';

interface Project {
  id: string;
  name: string;
  type: 'zip' | 'git';
  description?: string;
  path: string;
  createdAt: string;
  updatedAt: string;
  originalFile?: string;
  isActive: boolean;
}

interface ProjectWorkspaceProps {
  selectedProject?: Project;
  onProjectSelect: (project: Project | null) => void;
  onProjectCreate: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onProjectUpdate: (id: string, project: Partial<Project>) => Promise<void>;
  onProjectDelete: (id: string) => Promise<void>;
  onProjectClone: (project: Project) => Promise<void>;
  onProjectExport: (project: Project) => Promise<void>;
  onProjectImport: (file: File) => Promise<void>;
}

export function ProjectWorkspace({
  selectedProject,
  onProjectSelect,
  onProjectCreate,
  onProjectUpdate,
  onProjectDelete,
  onProjectClone,
  onProjectExport,
  onProjectImport
}: ProjectWorkspaceProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated'>('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'files' | 'editor' | 'terminal'>('overview');
  const [showGitIntegration, setShowGitIntegration] = useState(false);

  // Form state for creating/editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    githubRepo: '',
    workingDirectory: ''
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/projects');
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleZipUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/projects/create-from-zip', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Project created:', result);
        await loadProjects(); // Refresh the project list
      } else {
        const error = await response.json();
        console.error('Failed to create project:', error);
      }
    } catch (error) {
      console.error('Failed to upload ZIP:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      handleZipUpload(file);
    }
  };

  const handleGitProjectCreated = async (projectId: string) => {
    setShowGitIntegration(false);
    await loadProjects(); // Refresh the project list
    
    // Find and select the newly created project
    const newProject = projects.find(p => p.id === projectId);
    if (newProject) {
      onProjectSelect(newProject);
    }
  };

  const renderProjectList = () => {
    return (
      <div>
        {/* Search and Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label htmlFor="search">Search Projects</Label>
                <Input
                  id="search"
                  placeholder="Search by name or description..."
                  value={searchQuery}
                  onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                  className="mt-1"
                />
              </div>
              
              <div className="w-32">
                <Label htmlFor="filter">Status</Label>
                <select
                  id="filter"
                  value={filterActive}
                  onChange={(e) => setFilterActive((e.target as HTMLSelectElement).value as any)}
                  className="w-full mt-1 p-2 border rounded-md"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="w-32">
                <Label htmlFor="sort">Sort By</Label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy((e.target as HTMLSelectElement).value as any)}
                  className="w-full mt-1 p-2 border rounded-md"
                >
                  <option value="name">Name</option>
                  <option value="created">Created</option>
                  <option value="updated">Updated</option>
                </select>
              </div>
              
              <div className="w-24">
                <Label htmlFor="order">Order</Label>
                <select
                  id="order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder((e.target as HTMLSelectElement).value as any)}
                  className="w-full mt-1 p-2 border rounded-md"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.length === 0 ? (
            <div className="col-span-full">
              <Card>
                <CardContent className="p-8 text-center">
                  <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first project or upload a ZIP file to get started
                  </p>
                  <Button onClick={() => setIsCreating(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Project
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            projects.map((project) => (
              <Card key={project.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">{project.name}</CardTitle>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {project.isActive ? (
                        <Badge variant="default" className="text-xs">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                  </div>
                  
                  {project.description && (
                    <CardDescription className="text-sm">
                      {project.description}
                    </CardDescription>
                  )}
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Updated {new Date(project.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onProjectSelect(project)}
                      className="flex-1"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Open
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Project Overview</CardTitle>
              <CardDescription>
                Information about {selectedProject?.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Project Name</Label>
                  <p className="text-sm text-muted-foreground">{selectedProject?.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Type</Label>
                  <p className="text-sm text-muted-foreground">{selectedProject?.type}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Created</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedProject?.createdAt ? new Date(selectedProject.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedProject?.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      
      case 'files':
        return (
          <div className="h-96">
            <p className="text-muted-foreground">File Explorer will be integrated here</p>
          </div>
        );
      
      case 'editor':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Project Analysis & Development</h3>
                <p className="text-sm text-muted-foreground">
                  Use AI-powered analysis to understand your project and create development plans
                </p>
              </div>
            </div>
            
            {/* Project Analyst Component */}
            <ProjectAnalyst 
              project={selectedProject!} 
              onClose={() => setActiveTab('overview')}
            />
          </div>
        );
      
      case 'terminal':
        return (
          <div className="h-96">
            <p className="text-muted-foreground">Terminal will be integrated here</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  // If no project is selected, show the project list
  if (!selectedProject) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Projects</h2>
            <p className="text-sm text-muted-foreground">
              Manage your development workspaces and repositories
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              size="sm"
              className="gap-2"
            >
              <Archive className="h-4 w-4" />
              Upload ZIP
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={loadProjects}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button
              onClick={() => setShowGitIntegration(true)}
              size="sm"
              variant="outline"
              className="gap-2"
            >
              <GitBranch className="h-4 w-4" />
              Import from Git
            </Button>
            
            <Button
              onClick={() => setIsCreating(true)}
              size="sm"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileUp className="h-4 w-4 animate-pulse" />
                <span className="text-sm">Uploading and extracting ZIP file...</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project List */}
        {renderProjectList()}

        {/* Git Integration Modal */}
        {showGitIntegration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
              <GitIntegration
                onProjectCreated={handleGitProjectCreated}
                onClose={() => setShowGitIntegration(false)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // If a project is selected, show the project workspace with tabs
  return (
    <div className="space-y-4">
      {/* Project Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onProjectSelect(null)}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Projects
          </Button>
          <div>
            <h2 className="text-lg font-semibold">{selectedProject.name}</h2>
            <p className="text-sm text-muted-foreground">
              {selectedProject.type === 'zip' ? 'ZIP Project' : 'Git Repository'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant={selectedProject.isActive ? "default" : "secondary"}>
            {selectedProject.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      {/* Project Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: FolderOpen },
            { id: 'files', label: 'Files', icon: FileText },
            { id: 'editor', label: 'Editor', icon: Edit },
            { id: 'terminal', label: 'Terminal', icon: Terminal }
          ].map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {renderTabContent()}
      </div>
    </div>
  );
}
